import React, { useEffect, useState } from 'react';
import { MakeApiCallAsync } from '../../../helpers/ApiHelpers';
import Config from '../../../helpers/Config';
import { normalizeAddressName, parseProfileAddress, parsePinOffices } from '../../../helpers/ProfileAddress';
import { serializeBillingFields, validateBillingFields } from '../../../helpers/BillingAddressForm';

const fetchList = async (endpoint, parameters) => {
    const response = await MakeApiCallAsync(Config.END_POINT_NAMES[endpoint], null,
        {requestParameters: {...parameters, PageNo: 1, PageSize: 10000, recordValueJson: '[]'}},
        {Accept: 'application/json', 'Content-Type': 'application/json'}, 'POST', true);
    if (!response?.data?.data) throw new Error('Unable to load address options. Please try again.');
    return typeof response.data.data === 'string' ? JSON.parse(response.data.data) : response.data.data;
};

export default function BillingAddressFields({user, active, onChange}) {
    const [fields, setFields] = useState(() => {
        const address = parseProfileAddress(user.AddressLineOne);
        return {firstName: user.FirstName || '', lastName: user.LastName || '', phone: user.MobileNo || '',
            street: address.street, pin: String(user.PostalCode || ''), stateId: String(user.StateProvinceID ?? user.StateProvinceId ?? ''),
            state: user.StateName || '', cityId: String(user.CityID ?? user.CityId ?? ''), city: user.CityName || '',
            village: address.village, taluk: address.taluk, district: address.district};
    });
    const [states, setStates] = useState([]);
    const [cities, setCities] = useState([]);
    const [offices, setOffices] = useState([]);
    const [statesLoading, setStatesLoading] = useState(false);
    const [citiesLoading, setCitiesLoading] = useState(false);
    const [pinLoading, setPinLoading] = useState(false);
    const [error, setError] = useState('');
    const update = values => setFields(current => ({...current, ...values}));

    useEffect(() => {
        if (!active) return;
        let mounted = true;
        setStatesLoading(true);
        const load = async () => {
            let countryId = user.CountryID ?? user.CountryId;
            if (!countryId) {
                const countries = await fetchList('GET_COUNTRIES_LIST', {});
                countryId = countries.find(country => normalizeAddressName(country.CountryName) === 'india')?.CountryID;
            }
            const result = await fetchList('GET_STATES_PROVINCES_LIST', {CountryId: countryId});
            if (mounted) setStates(result);
        };
        load().catch(err => {if (mounted) setError(err.message);}).finally(() => {if (mounted) setStatesLoading(false);});
        return () => {mounted = false;};
    }, [active, user.CountryID, user.CountryId]);

    useEffect(() => {
        if (!active || !fields.stateId) {setCities([]); setCitiesLoading(false); return;}
        let mounted = true;
        setCitiesLoading(true); setCities([]);
        fetchList('GET_CITIES_LIST', {StateProvinceId: Number(fields.stateId)}).then(result => {
            if (mounted) setCities(result.filter(city => Number(city.StateProvinceID ?? city.StateProvinceId) === Number(fields.stateId)));
        }).catch(err => {if (mounted) setError(err.message);}).finally(() => {if (mounted) setCitiesLoading(false);});
        return () => {mounted = false;};
    }, [active, fields.stateId]);

    useEffect(() => {
        if (!active || !/^\d{6}$/.test(fields.pin) || !states.length) {setPinLoading(false); return;}
        const controller = new AbortController();
        const timer = setTimeout(async () => {
            setPinLoading(true); setError('');
            try {
                const response = await fetch('https://api.postalpincode.in/pincode/' + fields.pin, {signal: controller.signal});
                if (!response.ok) throw new Error('Billing PIN lookup is unavailable. Please try again.');
                const result = parsePinOffices(await response.json());
                if (!result.length) throw new Error('No address found for this billing PIN code.');
                if (controller.signal.aborted) return;
                const state = states.find(item => normalizeAddressName(item.StateName) === normalizeAddressName(result[0].State));
                if (!state) throw new Error('Select the billing state manually; the PIN state is missing from the list.');
                setOffices(result);
                setFields(current => {
                    const selected = result.find(office => office.Name === current.village) || (result.length === 1 ? result[0] : null);
                    return {...current, cityId: String(current.stateId) === String(state.StateProvinceID) ? current.cityId : '',
                        city: String(current.stateId) === String(state.StateProvinceID) ? current.city : '',
                        stateId: String(state.StateProvinceID), state: state.StateName,
                        district: result[0].District || '', village: selected?.Name || '',
                        taluk: selected?.Block && selected.Block !== 'NA' ? selected.Block : result[0].Block === 'NA' ? '' : result[0].Block || ''};
                });
            } catch (err) {if (!controller.signal.aborted) {setOffices([]); setError(err.message);}}
            finally {if (!controller.signal.aborted) setPinLoading(false);}
        }, 350);
        return () => {clearTimeout(timer); controller.abort();};
    }, [active, fields.pin, states]);

    useEffect(() => {
        if (!offices.length || !cities.length) return;
        const names = offices.flatMap(office => [office.District, office.Block, office.Name]).map(normalizeAddressName);
        const match = cities.find(city => names.includes(normalizeAddressName(city.CityName)));
        if (match) setFields(current => ({...current, cityId: String(match.CityID), city: match.CityName}));
    }, [offices, cities]);

    useEffect(() => {
        if (!active) return;
        const invalid = validateBillingFields(fields);
        onChange(statesLoading || citiesLoading || pinLoading || invalid ? '' : serializeBillingFields(fields));
    }, [active, fields, statesLoading, citiesLoading, pinLoading, onChange]);

    const input = (key, title, required = false) => <div className="form-group">
        <label htmlFor={'billing-' + key}>{title}{required && ' *'}</label>
        <input id={'billing-' + key} name={'billing-' + key} className="form-control" type="text" value={fields[key]}
            required={active && required} disabled={!active} maxLength={key === 'street' ? 600 : 150}
            onChange={event => update({[key]: event.target.value})} />
    </div>;
    return <div hidden={!active} className="checkout-modern__billing-fields">
        <div className="checkout-modern__billing-grid">
            {input('firstName', 'First name', true)}{input('lastName', 'Last name')}
            <div className="form-group">
                <label htmlFor="billing-state">State *</label>
                <select id="billing-state" name="billing-state" className="form-control" value={fields.stateId} required={active} disabled={!active || statesLoading || pinLoading}
                    onChange={event => {
                        const state = states.find(item => String(item.StateProvinceID) === event.target.value);
                        setOffices([]); setError('');
                        update({stateId: event.target.value, state: state?.StateName || '', cityId: '', city: '', village: '', taluk: '', district: '', pin: ''});
                    }}>
                    <option value="">{statesLoading ? 'Loading states...' : 'Select state'}</option>
                    {states.map(state => <option key={state.StateProvinceID} value={state.StateProvinceID}>{state.StateName}</option>)}
                </select>
            </div>
            <div className="form-group">
                <label htmlFor="billing-city">City / District *</label>
                <select id="billing-city" name="billing-city" className="form-control" value={fields.cityId} disabled={!active || !fields.stateId || citiesLoading || pinLoading}
                    required={active && !fields.district} onChange={event => {
                        const city = cities.find(item => String(item.CityID) === event.target.value);
                        update({cityId: event.target.value, city: city?.CityName || ''});
                    }}>
                    <option value="">{citiesLoading ? 'Loading cities...' : fields.district ? fields.district + ' (PIN district)' : 'Select city'}</option>
                    {cities.map(city => <option key={city.CityID} value={city.CityID}>{city.CityName}</option>)}
                </select>
            </div>
            {input('phone', 'Contact number')}
            <div className="form-group">
                <label htmlFor="billing-pin">PIN code *</label>
                <input id="billing-pin" name="billing-pin" className="form-control" type="text" inputMode="numeric" pattern="[0-9]{6}" maxLength={6}
                    value={fields.pin} required={active} disabled={!active} onChange={event => {
                        setOffices([]); setError('');
                        update({pin: event.target.value.replace(/\D/g, '').slice(0,6), village: '', taluk: '', district: '', cityId: '', city: ''});
                    }} />
            </div>
            <div className="form-group">
                <label htmlFor="billing-village">Village / Locality *</label>
                <select id="billing-village" name="billing-village" className="form-control" value={fields.village} required={active} disabled={!active || !offices.length || pinLoading}
                    onChange={event => {
                        const office = offices.find(item => item.Name === event.target.value);
                        update({village: event.target.value, taluk: office?.Block === 'NA' ? '' : office?.Block || '', district: office?.District || ''});
                    }}>
                    <option value="">Select village / locality</option>
                    {fields.village && !offices.some(office => office.Name === fields.village) && <option value={fields.village}>{fields.village}</option>}
                    {[...new Set(offices.map(office => office.Name))].map(name => <option key={name} value={name}>{name}</option>)}
                </select>
            </div>
            {input('taluk', 'Taluk / Block')}
        </div>
        {input('street', 'House / Building / Street address', true)}
        <small role="status" aria-live="polite">{pinLoading ? 'Looking up billing PIN code...' : error || 'Enter the PIN code, then select your village / locality.'}</small>
    </div>;
}
